const DAY = 86400000;

/** "2026-11-04" → a local noon Date, so a timezone never shifts the calendar day. */
export const toDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const todayISO = (offsetDays = 0) => new Date(Date.now() + offsetDays * DAY).toISOString().slice(0, 10);

/** Whole nights between two calendar days; 0 when the range is incomplete or backwards. */
export const nightsBetween = (checkIn, checkOut) => {
  const start = toDate(checkIn);
  const end = toDate(checkOut);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end - start) / DAY));
};

/** The room total for a stay — the nightly rate times the nights. */
export const quoteFor = (listing, checkIn, checkOut) => {
  const nights = nightsBetween(checkIn, checkOut);
  const rate = Number(listing?.price) || 0;
  return { nights, rate, total: nights * rate };
};

/** How many guests a stay sleeps, when the listing records its bedrooms. */
export const guestCapacity = (listing) => (listing?.bedrooms ? (Number(listing.bedrooms) + 1) * 2 : null);

/**
 * The three ways the guide groups a place to sleep. A property_type decides the
 * group, so an editor only picks the type and the section follows.
 */
export const STAY_GROUPS = [
  { id: "hotels", types: ["Hotel", "Parador", "Guesthouse", "Hostel"] },
  { id: "camping", types: ["Camping", "Glamping"] },
  { id: "unique", types: ["Unique stay", "Eco lodge", "Cabin"] },
];

export const STAY_TYPES = STAY_GROUPS.flatMap((group) => group.types);

/** Which section a stay belongs to: "hotels", "camping" or "unique". */
export const stayGroupOf = (listing) =>
  STAY_GROUPS.find((group) => group.types.includes(listing?.property_type))?.id || "unique";

export const isStayType = (type) => STAY_TYPES.includes(type);

const STAY_PARTNERS = [
  { match: /booking\.com/i, name: "Booking.com" },
  { match: /expedia\./i, name: "Expedia" },
  { match: /hotels\.com/i, name: "Hotels.com" },
  { match: /airbnb\./i, name: "Airbnb" },
  { match: /vrbo\./i, name: "Vrbo" },
  { match: /agoda\./i, name: "Agoda" },
];

/** "Booking.com", "Expedia"… — or null when the property books on its own site. */
export const stayPartnerName = (url) => STAY_PARTNERS.find((partner) => partner.match.test(url || ""))?.name || null;

/**
 * Informational hotel-site link for the promotional directory.
 * Only the official site root is used, never stored book-now, rate, or
 * reservation paths. Third-party booking aggregators are excluded.
 */
export const propertyWebsite = (listing) => {
  const raw = listing?.stay_url || listing?.website_url;
  if (!raw || !/^https?:\/\//i.test(raw)) return null;
  try {
    const url = new URL(raw);
    if (/booking\.com|expedia\.|hotels\.com|airbnb\.|vrbo\.|agoda\./i.test(url.hostname)) return null;
    if (!/^https?:$/.test(url.protocol)) return null;
    return url.origin + "/";
  } catch { return null; }
};

/** Legacy referral URL for other, non-hotel-showcase integrations. */
export const bookingLink = (listing) => {
  const raw = listing?.stay_url || listing?.booking_url || listing?.website_url;
  if (!raw || !/^https?:\/\//i.test(raw)) return null;
  try {
    const url = new URL(raw);
    url.searchParams.set("utm_source", "yatusabespr");
    url.searchParams.set("utm_medium", "referral");
    url.searchParams.set("utm_campaign", "local-stays");
    return url.href;
  } catch {
    return raw;
  }
};